/**
 * Схемы валидации ответов, которых нет в генерации Kubb.
 *
 * Правило: тип и схему к существующей ручке берём из `generated-kubb` —
 * рукописная копия переживает изменение контракта и продолжает компилироваться,
 * то есть врёт молча. Здесь остаётся только то, чего в спеке нет или что
 * приходится ослаблять относительно спеки (см. lenient-схему турнира ниже).
 *
 * KAN-1000: файл был 674 строки при 12 используемых схемах — остальные 87
 * экспортов не звал никто. Мёртвое удалено; при добавлении нового сюда
 * сначала проверьте, нет ли готовой схемы в generated-kubb/schemas.
 */
import { translateUi } from "@/shared/lib/ui-translate";
import { z } from "zod";
import {
  proNicknameStyleSchema,
  RiotAccountSchema,
  UserSearchResultSchema,
  UserTeamSchema,
} from "./user.schemas";
import { TournamentRegistrationSchema } from "./tournament.schemas";
import { goodLuckCoreDTOsTournamentParticipantDtoSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/tournamentParticipantDtoSchema";
import { goodLuckCoreDTOsTournamentReactionsDtoSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/tournamentReactionsDtoSchema";
import { goodLuckCoreDTOsTournamentResponseSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/tournamentResponseSchema";
import { goodLuckCoreDTOsTournamentWinnerDtoSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/tournamentWinnerDtoSchema";
import { goodLuckCoreDTOsUserNotificationSettingsDtoSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/userNotificationSettingsDtoSchema";
import { goodLuckCoreDTOsUserProfileDtoSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/userProfileDtoSchema";

// Бэкенд возвращает reactions/winner как null (турнир без реакций / без победителя),
// а сгенерённая Kubb-схема помечает их .optional() — только undefined, не null — и
// валидация ОТВЕТА падала при сохранении любых настроек турнира без победителя
// (ошибка «expected object, received null» на tournament.reactions / tournament.winner).
// Корень — в OpenAPI-спеке эти поля object без nullable; здесь релаксим схему,
// разрешая null. Ручной файл — регенерация Kubb это не затирает.
// TODO (backend): пометить reactions/winner как nullable в спеке, тогда релакс не нужен.
const goodLuckCoreDTOsTournamentResponseLenientSchema =
  z.lazy(() =>
  goodLuckCoreDTOsTournamentResponseSchema.extend({
    reactions: goodLuckCoreDTOsTournamentReactionsDtoSchema.nullish(),
    winner: goodLuckCoreDTOsTournamentWinnerDtoSchema.nullish(),
  }));

// KAN-938. Discord-уведомления настраиваются тремя категориями вместо списка типов
// событий. Бэкенд уже отдаёт discordTournaments/discordMatches/discordTeam, но Kubb-схема
// про них не знает — спеку перегенерируют только после деплоя на тест (`make api-sync-test`).
// zod-объект по умолчанию ВЫРЕЗАЕТ незнакомые ключи, поэтому без этого extend значения
// не доехали бы до UI даже с живого бэкенда. Поля объявлены ровно так, как их сгенерит
// Kubb (optional boolean), — после api-sync-test extend станет тождественным и не сломается.
const userNotificationSettingsWithCategoriesSchema =
  z.lazy(() =>
  goodLuckCoreDTOsUserNotificationSettingsDtoSchema.extend({
    discordTournaments: z.boolean().optional(),
    discordMatches: z.boolean().optional(),
    discordTeam: z.boolean().optional(),
    // KAN-1362: категория писем «Команды» — тот же приём, до api-sync-test Kubb о ней не знает.
    emailTeams: z.boolean().optional(),
  }));

export const userProfileDtoWithProNickSchema =
  z.lazy(() =>
  goodLuckCoreDTOsUserProfileDtoSchema.extend({
    notifications: userNotificationSettingsWithCategoriesSchema.optional(),
    hasPro: z.boolean().optional(),
    proNicknameStyle: proNicknameStyleSchema,
    proNicknameColorA: z.string().nullable().optional(),
    proNicknameColorB: z.string().nullable().optional(),
    // Custom status (доп. поля. После make api-sync-test станут частью базовой схемы.)
    customStatusText: z.string().nullable().optional(),
    customStatusEmoji: z.string().nullable().optional(),
    customStatusExpiresAtUtc: z.string().nullable().optional(),
    // Игровые роли для поиска команды (C2). После make api-sync-test переедут в базовую схему.
    preferredRole: z.string().nullable().optional(),
    secondaryRole: z.string().nullable().optional(),
  }));

// Схемы для авторизации
// Поиск пользователей и команды пользователя: ручки есть, схем в генерации нет.
export const SearchUsersResponseSchema = z.lazy(() =>
  z.object({
  users: z.array(UserSearchResultSchema),
}));

export const GetUserTeamsResponseSchema = z.lazy(() =>
  z.object({
  teams: z.array(UserTeamSchema),
}));

export const UpdateUserProfileRequestSchema = z.lazy(() =>
  z.object({
  displayName: z
    .string()
    .min(1, { error: () => translateUi("litResponseTypesSchemas.otobrazhaemoeImyaObyazatelno") })
    .max(100, { error: () => translateUi("litResponseTypesSchemas.otobrazhaemoeImyaNeDolzhnoPrevyshat100") })
    .optional(),
  about: z
    .string()
    .max(500, { error: () => translateUi("litResponseTypesSchemas.opisanieNeDolzhnoPrevyshat500Simvolov") })
    .optional(),
  location: z
    .string()
    .max(100, { error: () => translateUi("litResponseTypesSchemas.mestopolozhenieNeDolzhnoPrevyshat100Simvolov") })
    .optional(),
  website: z.string().url({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyUrlSayta") }).optional(),
  avatarUrl: z.string().url({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyUrlAvatara") }).optional(),
  banner: z.string().url({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyUrlBannera") }).optional(),
  languages: z.array(z.string()).optional(),
  status: z
    .enum(["online", "offline", "playing", "busy", "away", "searching", "invisible"])
    .optional(),
  socialLinks: z
    .object({
      x: z.string().url({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyUrlX") }).optional(),
      discord: z.string().optional(),
      youtube: z.string().url({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyUrlYoutube") }).optional(),
      telegram: z.string().optional(),
      twitch: z.string().url({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyUrlTwitch") }).optional(),
      steam: z.string().optional(),
      vk: z.string().url({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyUrlVk") }).optional(),
    })
    .partial()
    .optional(),
  privacy: z
    .object({
      showStats: z.boolean().optional(),
      showOnlineStatus: z.boolean().optional(),
      allowMessages: z.boolean().optional(),
      showInSearch: z.boolean().optional(),
    })
    .partial()
    .optional(),
  notifications: z
    .object({
      email: z.boolean().optional(),
      tournamentStart: z.boolean().optional(),
      newsAndRecommendations: z.boolean().optional(),
      emailTeams: z.boolean().optional(),
      discordEnabled: z.boolean().optional(),
      // KAN-938: discordSendAll/discordTypes доставку больше не определяют (наследие),
      // решают три категории ниже. Ключи оставлены, чтобы не ломать старые payload'ы.
      discordSendAll: z.boolean().optional(),
      discordTypes: z.array(z.string()).optional(),
      discordTournaments: z.boolean().optional(),
      discordMatches: z.boolean().optional(),
      discordTeam: z.boolean().optional(),
      preset: z.string().optional(),
      channelOverrides: z.array(z.object({
        eventType: z.string(),
        inApp: z.boolean().optional(),
        discord: z.boolean().optional(),
        email: z.boolean().optional(),
        soundForCritical: z.boolean().optional(),
      })).optional(),
    })
    .partial()
    .optional(),
  proNicknameStyle: proNicknameStyleSchema,
  proNicknameColorA: z
    .union([z.string().regex(/^#[0-9A-Fa-f]{6}$/), z.literal("")])
    .optional(),
  proNicknameColorB: z
    .union([z.string().regex(/^#[0-9A-Fa-f]{6}$/), z.literal("")])
    .optional(),
}));

export const UpdateUserProfileResponseSchema = z.lazy(() =>
  z.object({
  user: userProfileDtoWithProNickSchema,
}));

export const AddRiotAccountRequestSchema = z.lazy(() =>
  z.object({
  gameName: z
    .string()
    .min(1, { error: () => translateUi("litResponseTypesSchemas.imyaIgrokaObyazatelno") })
    .max(50, { error: () => translateUi("litResponseTypesSchemas.imyaIgrokaNeDolzhnoPrevyshat50") }),
  tagLine: z
    .string()
    .min(1, { error: () => translateUi("litResponseTypesSchemas.tegObyazatelen") })
    .max(10, { error: () => translateUi("litResponseTypesSchemas.tegNeDolzhenPrevyshat10Simvolov") }),
  region: z
    .string()
    .min(2, { error: () => translateUi("litResponseTypesSchemas.regionDolzhenSoderzhatMinimum2Simvola") })
    .max(10, { error: () => translateUi("litResponseTypesSchemas.regionNeDolzhenPrevyshat10Simvolov") }),
}));

export const AddRiotAccountResponseSchema = z.lazy(() =>
  z.object({
  account: RiotAccountSchema,
}));

export const SetMainRiotAccountResponseSchema = z.lazy(() =>
  z.object({
  account: RiotAccountSchema,
}));

// Схемы для турниров
export const CreateTournamentResponseSchema = z.lazy(() =>
  z.object({
  id: z.string(),
  success: z.boolean(),
}));

export const UpdateTournamentResponseSchema = z.lazy(() =>
  z.object({
  tournament: goodLuckCoreDTOsTournamentResponseLenientSchema,
}));

export const TournamentRegisterRequestSchema = z.lazy(() =>
  z.object({
  teamId: z.string().uuid({ error: () => translateUi("litResponseTypesSchemas.nekorrektnyyIdKomandy") }).optional(),
}));

export const TournamentRegisterResponseSchema = z.lazy(() =>
  z.object({
  success: z.boolean(),
  registration: TournamentRegistrationSchema,
}));

export const GetTournamentParticipantsResponseSchema = z.lazy(() =>
  z.array(
  goodLuckCoreDTOsTournamentParticipantDtoSchema,
));

export const GetUserRegistrationsResponseSchema = z.lazy(() =>
  z.object({
  registrations: z.array(TournamentRegistrationSchema),
}));

// Схемы для команд
export const CreateTeamResponseSchema = z.lazy(() =>
  z.object({
  id: z.string().min(1, { error: () => translateUi("litResponseTypesSchemas.idKomandyNeMozhetBytPustym") }),
  success: z.boolean().optional(),
}));
